import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  OutlinedInput
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MuiTelInput, MuiTelInputCountry } from 'mui-tel-input';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import {
  CURACAO_CITIES,
  EMAIL_PATTERN,
  organizationInfoErrorMessages
} from '@modules/organization/utils/organization.utils.ts';
import {
  CountryOption,
  OrganizationGeneralInfoFormValues,
  OrganizationGeneralInfoReqBody
} from '@modules/organization/models/organization.model.ts';
import OrganizationSectionCard from '@modules/organization/components/organization-section-card/organization-section-card.tsx';
import { ArrowDownIcon } from '@components/icons/svg-icon.tsx';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import {
  trimFormValues,
  validateNotOnlySpaces
} from '@utils/trim-form-values-utils.ts';
import { parsePhoneNumber } from '@utils/phone-number-utils.ts';
import countries from '@assets/data/countries.json';
import { getCities } from '@api/cities.api.ts';
import useProfileStore from '@store/profile-store/profile.store.ts';

const GeneralInfo: React.FC = () => {
  const { t } = useTranslation();
  const {
    profileData,
    currencies,
    getCurrencies,
    editOrganizationInfo,
    isOrganizationInfoLoading
  } = useProfileStore();
  const organization = profileData?.organization ?? null;
  const getErrorMessage = useErrorMessage();
  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    reset,
    setValue
  } = useForm<OrganizationGeneralInfoFormValues>({ mode: 'onTouched' });

  const [cities, setCities] = useState<string[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<string>('US');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCitiesByCountry = async (
    countryCode: string,
    namePrefix?: string
  ) => {
    if (!countryCode) return;

    setIsLoadingCities(true);
    try {
      if (countryCode === 'CW') {
        setCities(
          namePrefix
            ? CURACAO_CITIES.filter((city) =>
                city.toLowerCase().startsWith(namePrefix.toLowerCase())
              )
            : CURACAO_CITIES
        );
        return;
      }

      const res = await getCities(countryCode, namePrefix);
      setCities(res.map((c: { city: string }) => c.city));
    } catch {
      setCities([]);
    } finally {
      setIsLoadingCities(false);
    }
  };

  useEffect(() => {
    getCurrencies();
  }, []);

  useEffect(() => {
    if (!organization) return;

    const parsedPhone = parsePhoneNumber(organization.phone || '');
    const country = countries.find((c) => c.name === organization.country);

    reset({
      name: organization.name || '',
      email: organization.email || '',
      phone: parsedPhone.phone,
      city: organization.city || '',
      currency: organization.currency || null,
      country: country ?? null
    });

    if (parsedPhone.countryCode) {
      setPhoneCountry(parsedPhone.countryCode);
    } else if (country) {
      setPhoneCountry(country.code);
    }

    if (country) {
      setSelectedCountryCode(country.code);
      handleCitiesByCountry(country.code);
    }
  }, [organization, reset]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const handleCountryChange = (selectedCountry: CountryOption | null) => {
    setValue('city', '');
    setCities([]);

    if (selectedCountry?.code) {
      setSelectedCountryCode(selectedCountry.code);
      handleCitiesByCountry(selectedCountry.code);
      setPhoneCountry(selectedCountry.code);
      setValue('phone', '');
    } else {
      setSelectedCountryCode('');
    }
  };

  const handleCitySearch = (searchValue: string) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!selectedCountryCode) return;

    searchTimerRef.current = setTimeout(() => {
      handleCitiesByCountry(selectedCountryCode, searchValue);
    }, 300);
  };

  const onSubmit = (data: OrganizationGeneralInfoFormValues) => {
    const trimmedData = trimFormValues(data);

    const body: OrganizationGeneralInfoReqBody = {
      name: trimmedData.name,
      email: trimmedData.email,
      phone: trimmedData.phone,
      city: trimmedData.city,
      country: trimmedData.country?.name ?? '',
      currencyId: data.currency?.id ?? null,
      currencyCode: data.currency?.code ?? null,
      image: organization?.image ?? null
    };

    editOrganizationInfo({ data: body, id: organization?.id });
  };

  const getFieldError = (fieldName: keyof OrganizationGeneralInfoFormValues) =>
    errors?.[fieldName] &&
    getErrorMessage({
      errors,
      fieldName,
      errorMessages: organizationInfoErrorMessages
    });

  return (
    <OrganizationSectionCard
      title="organization_general_info_title"
      description="organization_general_info_description"
    >
      <form className="flex flex-col">
        <div className="grid grid-cols-3 gap-x-7 gap-y-3 max-lg-xl:grid-cols-2">
          <FormControl error={!!errors.name}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_name')}
              <InfoTooltip text="tooltip_info_name" />
            </FormLabel>
            <OutlinedInput
              {...register('name', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              placeholder={t('placeholder_name')}
            />
            <FormHelperText>{getFieldError('name')}</FormHelperText>
          </FormControl>

          <FormControl error={!!errors.email}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_form_email')}
              <InfoTooltip text="tooltip_info_email" />
            </FormLabel>
            <OutlinedInput
              {...register('email', {
                required: true,
                pattern: EMAIL_PATTERN
              })}
              placeholder={t('placeholder_email')}
            />
            <FormHelperText>{getFieldError('email')}</FormHelperText>
          </FormControl>

          <FormControl error={!!errors.country}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_country')}
              <InfoTooltip text="tooltip_info_country" />
            </FormLabel>
            <Controller
              name="country"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  popupIcon={
                    <ArrowDownIcon sx={{ width: '18px !important' }} />
                  }
                  options={countries}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.code === value.code
                  }
                  onChange={(_, value) => {
                    field.onChange(value);
                    handleCountryChange(value);
                  }}
                  value={field.value || null}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props;

                    return (
                      <li key={key} {...restProps}>
                        <span className="option-label">{option.name}</span>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.country}
                      placeholder={t('placeholder_country')}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>{getFieldError('country')}</FormHelperText>
          </FormControl>

          <FormControl error={!!errors.city}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_city')}
              <InfoTooltip text="tooltip_info_city" />
            </FormLabel>
            <Controller
              name="city"
              control={control}
              rules={{
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  popupIcon={
                    <ArrowDownIcon sx={{ width: '18px !important' }} />
                  }
                  options={cities}
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={(_, value) => field.onChange(value)}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      handleCitySearch(newInputValue);
                    }
                  }}
                  value={field.value || null}
                  loading={isLoadingCities}
                  disabled={!selectedCountryCode && !isLoadingCities}
                  filterOptions={(options) => options}
                  renderOption={(props, option, { index }) => {
                    const { key: _key, ...restProps } = props;

                    return (
                      <li key={`${option}-${index}`} {...restProps}>
                        <span className="option-label">{option}</span>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.city}
                      placeholder={
                        !selectedCountryCode && !isLoadingCities
                          ? t('placeholder_select_conutry')
                          : cities.length > 0
                            ? t('placeholder_select_city')
                            : t('placeholder_city')
                      }
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: isLoadingCities ? (
                            <InputAdornment position="end">
                              <CircularProgress
                                className="absolute right-5"
                                size={16}
                              />
                            </InputAdornment>
                          ) : (
                            params.InputProps?.endAdornment
                          )
                        }
                      }}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>{getFieldError('city')}</FormHelperText>
          </FormControl>

          <FormControl error={!!errors.currency}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_currency')}
              <InfoTooltip text="tooltip_info_currency" />
            </FormLabel>
            <Controller
              name="currency"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  popupIcon={
                    <ArrowDownIcon sx={{ width: '18px !important' }} />
                  }
                  options={currencies}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.symbol})`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.code === value.code
                  }
                  onChange={(_, value) => field.onChange(value)}
                  value={field.value || null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.currency}
                      placeholder={t('placeholder_select_currency')}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>{getFieldError('currency')}</FormHelperText>
          </FormControl>

          <FormControl error={!!errors.phone}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_phone_number')}
              <InfoTooltip text="tooltip_info_phone_number" />
            </FormLabel>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              }}
              render={({ field }) => (
                <MuiTelInput
                  {...field}
                  fullWidth
                  placeholder={t('placeholder_phone_number')}
                  key={`${phoneCountry}-${organization?.id || 'new'}`}
                  defaultCountry={phoneCountry as MuiTelInputCountry}
                  preferredCountries={['AD', 'US', 'GB', 'DE', 'FR']}
                  forceCallingCode={true}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiTelInput-MenuItem': {
                          justifyContent: 'flex-start !important'
                        },
                        '& .MuiTelInput-ListItemText-country': {
                          flex: 'none !important'
                        }
                      }
                    }
                  }}
                />
              )}
            />
            <FormHelperText>{getFieldError('phone')}</FormHelperText>
          </FormControl>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button
            variant="contained"
            loading={isOrganizationInfoLoading()}
            onClick={handleSubmit(onSubmit)}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </OrganizationSectionCard>
  );
};

export default GeneralInfo;
